import { IncorporationRequestDataType } from '@commenda/types';
import { ProceedIcon } from '@commenda/icons';
import { Button, Loading, MultiSelect, Switch } from '@commenda/kakashi';
import { TSetNotification } from '@commenda/types/src/random';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { getPrintableDirectorName } from '@commenda/utils';
import { CorporationType, Currency, StakeholderType } from '@prisma/client';
import { KeyedMutator } from 'swr';
import { IncorporationFormHeading } from '../../common';
import CorporateStakeholderModal from '../../common/corporateStakeholder/CorporateStakeholderModal';
import { CorporateShareholderEquitySplitDistribution } from '../../common/corporateStakeholder/EquitySplitDistribution';
import StakeholderOptionsModal from '../../common/corporateStakeholder/StakeholderOptionsModal';
import DirectorModal from '../../Director/new/DirectorModal';
import CapTableKS from '../../EquityInfo/CapTable';
import CompanyValuationInputKS from '../../EquityInfo/CompanyValuationInputKS';
import EsopPoolInputKS from '../../EquityInfo/EsopPoolInputKS';
import { useEquityFormState } from '../../EquityInfo/hooks/useEquityFormState';
import { useEquityResources } from '../../EquityInfo/hooks/useEquityResources';
import ShareCapitalSummary from '../../EquityInfo/ShareCapitalSummary';
import DirectorForm from '../../us/USDirectorForm';
import { validateUSStakeholders } from '../../us/validators';

const EquityInfo = ({
  next,
  previous,
  data,
  mutate,
  setNotification,
  token,
  companyId,
  corporationId,
  readonly = false,
}: {
  next?: () => void;
  previous?: () => void;
  data: WorkRequestResponseType & { requestData: IncorporationRequestDataType };
  mutate: KeyedMutator<unknown>;
  setNotification?: TSetNotification;
  token: string;
  companyId: number;
  corporationId: number;
  readonly?: boolean;
}) => {
  const {
    directorsData,
    corporations,
    capTableTransactions,
    stockClasses,
    mutateDirectors,
    mutateCorporations,
    mutateCapTableTransactions,
    stakeholderOptions,
    isLoading,
    isError,
  } = useEquityResources(companyId, corporationId);

  const {
    equityInfo,
    setEquityInfo,
    selectedStakeholders,
    setSelectedStakeholders,
    setCorporateShareholderToEdit,
    corporateStakeholderModalVisibility,
    directorModalVisibility,
    setHasESOPs,
    hasESOPs,
    stakeholderIdsWithError,
    setStakeholderIdsWithError,
    setCapTableTransactionIdsToDelete,
    isSubmitting,
    stakeholderTypeModalVisibility,
    onAddCorporateShareholder,
    onSubmit,
    selectAddedShareholder,
    onChangeShareholder,
    individualShareholderToEdit,
    corporateShareholderToEdit,
    capTableData,
    onEditShareholderDetails,
  } = useEquityFormState({
    capTableTransactions,
    stockClasses,
    directorsData: directorsData?.directors,
    corporations,
    mutateCorporations,
    resetEditModalPresetState: () => {},
    corporationId,
    config: {
      defaultShareCount: 10_000_000,
      defaultSharePrice: 0.00001,
      currency: Currency.USD,
    },
    onSubmitSuccess: () => {
      mutate();
      mutateCapTableTransactions();
      next?.();
    },
    validateStakeholdersAndGetIdsWithError: validateUSStakeholders,
  });

  if (isLoading || isError) {
    return <Loading />;
  }

  return (
    <div className="flex flex-col gap-10 max-w-[700px]">
      <IncorporationFormHeading
        text={
          data?.requestData?.corporationType === CorporationType.LLC
            ? 'Allocate company ownership'
            : 'Company Ownership'
        }
        additionalText={
          data?.requestData?.corporationType === CorporationType.LLC
            ? "Please provide information about this company's members."
            : "Please provide information about this corporation's shareholders."
        }
      />

      <div>
        {data?.requestData?.corporationType === CorporationType.LLC ? (
          <CompanyValuationInputKS
            equityInfo={equityInfo}
            setEquityInfo={setEquityInfo}
            shareCountPlaceholder="10,000,000"
            sharePricePlaceholder="0.00001"
            currency={{
              countryCode: 'en-US',
              name: 'USD',
              style: '$',
            }}
            helpTexts={{
              mainHeading: 'Capital contributions',
              bodyText:
                'We suggest you keep the default values as they are standard for LLC in the United States.',
              connectingText: 'capital contributions valued at',
            }}
            disabled={readonly}
          />
        ) : (
          <CompanyValuationInputKS
            equityInfo={equityInfo}
            setEquityInfo={setEquityInfo}
            shareCountPlaceholder="10,000,000"
            sharePricePlaceholder="0.00001"
            currency={{
              countryCode: 'en-US',
              name: 'USD',
              style: '$',
            }}
            helpTexts={{
              mainHeading: 'Authorized Shares',
              bodyText:
                'We suggest you keep the default values as they are standard for corporations in the United States.',
              connectingText: 'shares valued at',
            }}
            disabled={readonly}
          />
        )}
      </div>

      <div className=" flex flex-col gap-4">
        {data?.requestData?.corporationType === CorporationType.LLC ? (
          <div>
            <h3 className="text-base">Membership Interest Allocation</h3>
            <span className="text-sm text-neutral-2">
              Total allocation must add up to 100%.
            </span>
          </div>
        ) : (
          <div>
            <h3 className="text-base">Shareholder Equity</h3>
          </div>
        )}
        {data?.requestData?.corporationType === CorporationType.LLC ? null : (
          <div className="flex gap-2">
            <Switch
              checked={hasESOPs}
              defaultChecked={hasESOPs}
              onCheckedChange={(val: boolean) => {
                if (!val) {
                  setHasESOPs(false);
                  setEquityInfo((prev) => ({ ...prev, shareRetained: 0 }));
                  return;
                }
                setHasESOPs(true);
              }}
              disabled={readonly}
            />
            <span>My company has an employee stock option pool (ESOP)</span>
          </div>
        )}
      </div>
      {hasESOPs &&
      data?.requestData?.corporationType === CorporationType.CCorp ? (
        <div className="flex flex-col gap-4">
          <EsopPoolInputKS
            equityInfo={equityInfo}
            setEquityInfo={setEquityInfo}
            readOnly={readonly}
          />
        </div>
      ) : null}

      <div className=" flex flex-col gap-4 pb-12 border-b">
        {hasESOPs &&
        data?.requestData?.corporationType === CorporationType.CCorp ? (
          <div>
            <h3 className="text-base">Ownership Pool</h3>
          </div>
        ) : null}
        <MultiSelect
          disabled={readonly}
          defaultValue={Object.keys(selectedStakeholders || {})}
          id="shareholder-multiselect"
          placeholder={
            data?.requestData?.corporationType === CorporationType.LLC
              ? 'Select members'
              : 'Select shareholders'
          }
          options={
            stakeholderOptions
              ?.map((stakeholder) => {
                return {
                  label: stakeholder.name,
                  value: stakeholder.id,
                };
              })
              ?.sort((a, b) => a.label.localeCompare(b.label)) || []
          }
          onValueChange={onChangeShareholder}
          hideSelectAll
          hideTags
          addItemConfig={{
            shouldAllowAdd: true,
            placeholder:
              data?.requestData?.corporationType === CorporationType.CCorp
                ? 'Add a shareholder'
                : 'Add a member',
            onAddItem: stakeholderTypeModalVisibility.show,
          }}
        />
        <StakeholderOptionsModal
          isShown={stakeholderTypeModalVisibility.visible}
          toggle={stakeholderTypeModalVisibility.hide}
          onShowDirectorModal={directorModalVisibility.show}
          onShowCorporateShareholderModal={() => {
            stakeholderTypeModalVisibility.hide();
            corporateStakeholderModalVisibility.show();
          }}
        />
        <CorporateShareholderEquitySplitDistribution
          selectedStakeholders={selectedStakeholders}
          setSelectedStakeholders={setSelectedStakeholders}
          equityInfo={equityInfo}
          onChangeShareholder={onChangeShareholder}
          onEditShareholderDetails={onEditShareholderDetails}
          stakeholderIdsWithError={Array.from(stakeholderIdsWithError)}
          setNotification={setNotification}
          disabled={readonly}
          setCapTableTransactionIdsToDelete={setCapTableTransactionIdsToDelete}
        />
      </div>

      <div className="flex flex-col pt-2 gap-4">
        {data?.requestData?.corporationType === CorporationType.CCorp ? (
          <h3 className="text-base">Ownership Summary</h3>
        ) : null}
        <CapTableKS
          data={capTableData}
          showEsopPool={
            hasESOPs &&
            data?.requestData?.corporationType === CorporationType.CCorp
          }
          isNumberOfSharesApplicable={
            data?.requestData?.corporationType === CorporationType.CCorp
          }
          shareValueCurrency={
            data?.requestData?.corporationType === CorporationType.CCorp
              ? Currency.USD
              : undefined
          }
        />
        {data?.requestData?.corporationType === CorporationType.CCorp ? (
          <ShareCapitalSummary
            authorizedShares={equityInfo.shareCount}
            pricePerShare={equityInfo.price}
            capTableData={capTableData}
            currency={Currency.USD}
          />
        ) : null}
      </div>

      <div className="flex gap-4">
        {previous ? (
          <Button variant="secondary" onClick={previous}>
            <ProceedIcon className="rotate-180 mr-2 h-4 w-4" />
            <span>Back</span>
          </Button>
        ) : null}
        {next ? (
          <Button onClick={onSubmit} disabled={isSubmitting}>
            <span>Continue</span>
            <ProceedIcon className="ml-2 h-4 w-4" />
          </Button>
        ) : null}
      </div>
      <DirectorModal
        isOpen={directorModalVisibility.visible}
        director={individualShareholderToEdit}
        setOpen={directorModalVisibility.hide}
        companyDataMutate={mutateDirectors}
        DirectorForm={DirectorForm}
        setNotification={setNotification}
        companyId={companyId}
        corporationId={corporationId}
        token={token}
        workRequestData={data}
        modalConfig={{
          heading: individualShareholderToEdit
            ? data?.requestData?.corporationType === CorporationType.CCorp
              ? 'Edit individual shareholder'
              : 'Edit member'
            : data?.requestData?.corporationType === CorporationType.CCorp
              ? 'Add an individual shareholder'
              : 'Add a member',
          postSave: (dir) => {
            stakeholderTypeModalVisibility.hide();
            selectAddedShareholder({
              type: StakeholderType.INDIVIDUAL,
              id: dir.id,
              name: getPrintableDirectorName(dir),
            });
            setCorporateShareholderToEdit(undefined);
            setStakeholderIdsWithError((prev) => {
              const newSet = new Set(prev);
              newSet.delete(`INDIVIDUAL_${dir.id}`);
              return newSet;
            });
          },
        }}
      />

      <CorporateStakeholderModal
        selectedCorporateStakeholderId={corporateShareholderToEdit}
        setSelectedCorporateStakeholderId={setCorporateShareholderToEdit}
        companyId={companyId}
        isShown={corporateStakeholderModalVisibility.visible}
        toggle={corporateStakeholderModalVisibility.hide}
        postSave={({
          corporationId: corporateStakeholderId,
          corporationName,
        }) =>
          onAddCorporateShareholder({
            corporateStakeholderId,
            corporationName,
          })
        }
        mutate={async () => {
          await mutateCorporations();
        }}
        setNotification={setNotification}
        DirectorForm={DirectorForm}
        directors={directorsData?.directors || []}
        mutateDirectors={mutateDirectors}
        corporations={corporations || []}
        headerTextOverride={
          corporateShareholderToEdit
            ? data?.requestData?.corporationType === CorporationType.CCorp
              ? 'Edit corporate shareholder'
              : 'Edit corporate member'
            : data?.requestData?.corporationType === CorporationType.CCorp
              ? 'Add a corporate shareholder'
              : 'Add a corporate member'
        }
        workRequestData={data}
      />
    </div>
  );
};

export default EquityInfo;
